import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { TrendingDown, TrendingUp, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CapTableEntry {
  shareholder_id: string;
  shareholder_name: string;
  total_shares: number;
  ownership_pct: number;
}

interface Subscription {
  id: string;
  shareholder_id: string | null;
  shares_offered: number;
  price_per_share: number;
  total_amount: number;
  status: string;
  share_class: string;
}

const COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export function SubscriptionCapTablePreview({ companyId }: { companyId: string }) {
  const [currentCapTable, setCurrentCapTable] = useState<CapTableEntry[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareholderId, setShareholderId] = useState<string | null>(null);

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Get current user's shareholder ID
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: shareholderData } = await supabase
        .from('shareholders')
        .select('id')
        .eq('company_id', companyId)
        .eq('email', user.email)
        .single();

      setShareholderId(shareholderData?.id || null);

      // Fetch current cap table
      const { data: capTableData, error: capError } = await supabase
        .from('cap_table_summary')
        .select('*')
        .eq('company_id', companyId);

      if (capError) throw capError;

      // Calculate ownership percentages
      const totalShares =
        capTableData?.reduce((sum, entry) => sum + Number(entry.total_shares), 0) || 0;
      const capTableWithPct =
        capTableData?.map((entry) => ({
          shareholder_id: entry.shareholder_id || '',
          shareholder_name: entry.shareholder_name || 'Unknown',
          total_shares: Number(entry.total_shares),
          ownership_pct: totalShares > 0 ? (Number(entry.total_shares) / totalShares) * 100 : 0,
        })) || [];

      setCurrentCapTable(capTableWithPct);

      // Fetch pending subscriptions
      const { data: subsData, error: subsError } = await supabase
        .from('subscription_agreements')
        .select('*, shareholders(name)')
        .eq('company_id', companyId)
        .in('status', ['OPEN', 'SUBSCRIBED', 'PAID']);

      if (subsError) throw subsError;

      setSubscriptions(subsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load cap table data');
    } finally {
      setLoading(false);
    }
  };

  const projectedCapTable = useMemo(() => {
    if (!subscriptions.length) return currentCapTable;

    // Calculate total new shares from all subscriptions
    const newSharesByHolder = subscriptions.reduce(
      (acc, sub) => {
        const holderId = sub.shareholder_id || 'new';
        acc[holderId] = (acc[holderId] || 0) + sub.shares_offered;
        return acc;
      },
      {} as Record<string, number>
    );

    // Current total shares
    const currentTotal = currentCapTable.reduce((sum, entry) => sum + entry.total_shares, 0);
    const newTotal =
      currentTotal + Object.values(newSharesByHolder).reduce((sum, shares) => sum + shares, 0);

    // Build projected cap table
    const projected = [...currentCapTable];

    // Update existing shareholders with new shares
    Object.entries(newSharesByHolder).forEach(([holderId, newShares]) => {
      const existingIndex = projected.findIndex((e) => e.shareholder_id === holderId);
      if (existingIndex >= 0) {
        projected[existingIndex] = {
          ...projected[existingIndex],
          total_shares: projected[existingIndex].total_shares + newShares,
          ownership_pct: ((projected[existingIndex].total_shares + newShares) / newTotal) * 100,
        };
      } else {
        // New shareholder
        const sub = subscriptions.find((s) => s.shareholder_id === holderId);
        projected.push({
          shareholder_id: holderId,
          shareholder_name: (sub as any)?.shareholders?.name || 'New Investor',
          total_shares: newShares,
          ownership_pct: (newShares / newTotal) * 100,
        });
      }
    });

    // Recalculate percentages for all shareholders
    return projected.map((entry) => ({
      ...entry,
      ownership_pct: (entry.total_shares / newTotal) * 100,
    }));
  }, [currentCapTable, subscriptions]);

  const mySubscriptions = subscriptions.filter((s) => s.shareholder_id === shareholderId);
  const myCurrentOwnership =
    currentCapTable.find((e) => e.shareholder_id === shareholderId)?.ownership_pct || 0;
  const myProjectedOwnership =
    projectedCapTable.find((e) => e.shareholder_id === shareholderId)?.ownership_pct || 0;

  const dilutionImpact = useMemo(() => {
    return currentCapTable
      .map((entry) => {
        const projected = projectedCapTable.find((p) => p.shareholder_id === entry.shareholder_id);
        const change = projected
          ? projected.ownership_pct - entry.ownership_pct
          : -entry.ownership_pct;
        return {
          name: entry.shareholder_name,
          current: entry.ownership_pct,
          projected: projected?.ownership_pct || 0,
          change,
        };
      })
      .sort((a, b) => b.current - a.current);
  }, [currentCapTable, projectedCapTable]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading cap table preview...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (!subscriptions.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Pending Subscriptions</CardTitle>
          <CardDescription>
            There are currently no pending subscription agreements for this company.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {mySubscriptions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Your Subscription Impact</CardTitle>
            <CardDescription>How your subscription will affect your ownership</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="text-sm text-muted-foreground">Current Ownership</div>
                <div className="text-2xl font-bold">{myCurrentOwnership.toFixed(2)}%</div>
              </div>
              <div className="p-4 bg-primary/10 rounded-lg">
                <div className="text-sm text-muted-foreground">Projected Ownership</div>
                <div className="text-2xl font-bold text-primary">
                  {myProjectedOwnership.toFixed(2)}%
                </div>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <div className="text-sm text-muted-foreground">Change</div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold">
                    {myProjectedOwnership > myCurrentOwnership ? '+' : ''}
                    {(myProjectedOwnership - myCurrentOwnership).toFixed(2)}%
                  </span>
                  {myProjectedOwnership > myCurrentOwnership ? (
                    <TrendingUp className="h-5 w-5 text-green-500" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-red-500" />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {mySubscriptions.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between p-3 bg-muted rounded"
                >
                  <div>
                    <div className="font-medium">{sub.shares_offered.toLocaleString()} shares</div>
                    <div className="text-sm text-muted-foreground">{sub.share_class}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">${sub.total_amount.toLocaleString()}</div>
                    <Badge variant="secondary">{sub.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Cap Table Comparison</CardTitle>
          <CardDescription>
            Current ownership vs projected ownership after subscriptions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="charts" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="charts">Ownership Charts</TabsTrigger>
              <TabsTrigger value="dilution">Dilution Impact</TabsTrigger>
            </TabsList>

            <TabsContent value="charts" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-center">Current Cap Table</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={currentCapTable}
                        dataKey="ownership_pct"
                        nameKey="shareholder_name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(entry) =>
                          `${entry.shareholder_name}: ${entry.ownership_pct.toFixed(1)}%`
                        }
                      >
                        {currentCapTable.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4 text-center">Projected Cap Table</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={projectedCapTable}
                        dataKey="ownership_pct"
                        nameKey="shareholder_name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(entry) =>
                          `${entry.shareholder_name}: ${entry.ownership_pct.toFixed(1)}%`
                        }
                      >
                        {projectedCapTable.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="dilution" className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <p className="text-sm">
                  This shows how each shareholder's ownership percentage will change after all
                  pending subscriptions are completed.
                </p>
              </div>

              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={dilutionImpact}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis label={{ value: 'Ownership %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
                  <Legend />
                  <Bar dataKey="current" fill="hsl(var(--muted))" name="Current %" />
                  <Bar dataKey="projected" fill="hsl(var(--primary))" name="Projected %" />
                </BarChart>
              </ResponsiveContainer>

              <div className="space-y-2">
                <h4 className="font-semibold">Detailed Impact</h4>
                {dilutionImpact.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between p-3 bg-muted rounded"
                  >
                    <span className="font-medium">{item.name}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-sm">
                        {item.current.toFixed(2)}% → {item.projected.toFixed(2)}%
                      </span>
                      <Badge variant={item.change > 0 ? 'default' : 'secondary'}>
                        {item.change > 0 ? '+' : ''}
                        {item.change.toFixed(2)}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
